import { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import { MESSAGE_TYPES } from "../../../utils/editUtils";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Campaign", path: `/campaign` },
];

function CampaignEditPage() {
  const contentKey = "campaignContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");

  const [campaign, setCampaign] = useState({
    title: "",
    description: "",
    prices: [],
  });

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // iframe通信ヘルパー
  const sendToIframe = (messageData) => {
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        messageData,
        process.env.REACT_APP_ORIGIN
      );
    }
  };

  // iframeスクロール命令
  const scrollIframeTo = (targetSelector) => {
    sendToIframe({
      type: MESSAGE_TYPES.SCROLL_TO,
      target: targetSelector,
      contentKey,
    });
  };

  // 全フィールド空のエントリかどうか判定
  const isPriceEmpty = (p) =>
    !(p.title || "").trim() &&
    !(p.old || "").trim() &&
    !(p.new || "").trim();

  // データ取得
  const fetchCampaignData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_campaign/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      const rawPrices = Array.isArray(data.prices) ? data.prices : [];
      // 空の場合はデフォルトで1行表示
      const prices =
        rawPrices.length > 0 ? rawPrices : [{ title: "", old: "", new: "" }];
      setCampaign({
        title: data.title || "",
        description: data.description || "",
        prices,
      });
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchCampaignData();
  }, [fetchCampaignData]);

  // 共通: ステート更新 + iframe通知
  const updateCampaign = (nextCampaign) => {
    setCampaign(nextCampaign);
    // プレビューには空エントリを送らない（空の料金行が表示されないように）
    const filteredForPreview = {
      ...nextCampaign,
      prices: (nextCampaign.prices || []).filter((p) => !isPriceEmpty(p)),
    };
    sendToIframe({
      type: MESSAGE_TYPES.TEXT_UPDATE,
      contentKey,
      allData: filteredForPreview,
    });
  };

  // タイトル・説明の変更
  const handleFieldChange = (field, value) => {
    updateCampaign({ ...campaign, [field]: value });
  };

  // 料金項目の変更
  const handlePriceChange = (index, field, value) => {
    const newPrices = campaign.prices.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    );
    updateCampaign({ ...campaign, prices: newPrices });
  };

  // 料金項目の追加
  const handleAddPrice = (index) => {
    const newPrices = [...campaign.prices];
    newPrices.splice(index + 1, 0, { title: "", old: "", new: "" });
    updateCampaign({ ...campaign, prices: newPrices });
  };

  // 料金項目の削除（最低1行は残す）
  const handleDeletePrice = (index) => {
    const filtered = campaign.prices.filter((_, i) => i !== index);
    const newPrices =
      filtered.length > 0 ? filtered : [{ title: "", old: "", new: "" }];
    updateCampaign({ ...campaign, prices: newPrices });
  };

  // 更新
  const handleSubmit = async () => {
    setLoadingText("保存中");
    // 空エントリを除外した料金で保存
    const payload = {
      ...campaign,
      prices: (campaign.prices || []).filter((p) => !isPriceEmpty(p)),
    };
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_campaign/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page_content: payload }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }
      setMessage(data.message);
    } catch (error) {
      console.error("保存エラー:", error);
    }
    setLoadingText("");
  };

  // リセット
  const handleReset = () => {
    fetchCampaignData();
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            {/* 編集フォーム */}
            <div className="form">
              <section>
                <h4>キャンペーン</h4>
                <div className="area1">
                  {/* タイトル */}
                  <input
                    type="text"
                    className="input1"
                    value={campaign.title}
                    placeholder="タイトルを入力（例：4月のキャンペーン）"
                    onChange={(e) =>
                      handleFieldChange("title", e.target.value)
                    }
                    onFocus={() => scrollIframeTo(".edit_campaign_title")}
                    data-label="キャンペーン タイトル"
                  />

                  {/* 説明 */}
                  <textarea
                    className="input2"
                    value={campaign.description}
                    placeholder="説明を入力（例：4月に入会いただくと入会金が0円になります。）"
                    rows={3}
                    onChange={(e) =>
                      handleFieldChange("description", e.target.value)
                    }
                    onFocus={() => scrollIframeTo(".edit_campaign_description")}
                    data-label="キャンペーン 説明"
                  />
                </div>
              </section>

              <section>
                <h4>料金項目</h4>
                <div className="area2">
                  {campaign.prices.map((price, index) => (
                    <div className="item" key={index}>
                      <div className="ctrl">
                        <p>{index + 1}</p>
                        <div className="icon-wrap">
                          <i
                            className="icon delete"
                            onClick={() => handleDeletePrice(index)}
                          ></i>
                          <i
                            className="icon add"
                            onClick={() => handleAddPrice(index)}
                          ></i>
                        </div>
                      </div>
                      <div
                        className="forms"
                        style={{
                          display: "flex",
                          gap: "0.5em",
                          flexWrap: "wrap",
                        }}
                      >
                        <input
                          type="text"
                          value={price.title || ""}
                          placeholder="料金タイトル（例：入会金）"
                          onChange={(e) =>
                            handlePriceChange(index, "title", e.target.value)
                          }
                          onFocus={() =>
                            scrollIframeTo(".edit_campaign_prices")
                          }
                          data-label={`料金${index + 1} タイトル`}
                          style={{ flex: "1 1 0", minWidth: "0" }}
                        />
                        <input
                          type="text"
                          value={price.old || ""}
                          placeholder="元の料金（例：¥5,000）"
                          onChange={(e) =>
                            handlePriceChange(index, "old", e.target.value)
                          }
                          onFocus={() =>
                            scrollIframeTo(".edit_campaign_prices")
                          }
                          data-label={`料金${index + 1} 元の金額`}
                          style={{ flex: "1 1 0", minWidth: "0" }}
                        />
                        <input
                          type="text"
                          value={price.new || ""}
                          placeholder="キャンペーン料金（例：¥0）"
                          onChange={(e) =>
                            handlePriceChange(index, "new", e.target.value)
                          }
                          onFocus={() =>
                            scrollIframeTo(".edit_campaign_prices")
                          }
                          data-label={`料金${index + 1} 新の金額`}
                          style={{ flex: "1 1 0", minWidth: "0" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* 送信ボタン */}
            <div className="submit-area">
              <button type="reset" onClick={handleReset}>
                Reset
              </button>
              <button type="submit" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>

      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      {/* プレビュー */}
      <Iframe path={"/newvisitor"} data={campaign} contentKey={contentKey} />
    </>
  );
}

export default CampaignEditPage;
